export enum UserRole {
  Normal = 'Normal',
  AdminA = 'AdminA', // Country A: Turkey (deprecated, kept for backward compatibility)
  AdminB = 'AdminB', // Country B: Iraq (deprecated, kept for backward compatibility)
  SuperAdmin = 'SuperAdmin',
  Office = 'Office', // Office account
  Worker = 'Worker', // Worker account (belongs to an office)
}

export interface User {
  id: string;
  name: string;
  surname?: string; // Surname/last name
  email: string;
  phone?: string; // Phone number (with country code)
  phoneVerified?: boolean; // Whether phone number is verified
  role: UserRole;
  balance: number;
  officeId?: string; // For workers and offices - identifies which office they belong to
  officeName?: string; // Office name (for office accounts)
  username?: string; // Username for login (for offices)
  password?: string; // Password for login (for offices, should be hashed in production)
  tcKimlikNo?: string; // Turkish ID number (T.C. Kimlik No)
  profilePicture?: string; // Profile picture identifier (default avatar number)
  archived?: boolean; // Whether the user is archived (soft delete)
  archivedAt?: string; // Timestamp when the user was archived
  approved?: boolean; // Whether the user account is approved (for Normal users)
  approvedAt?: string; // Timestamp when the user was approved
  approvedBy?: string; // Office ID or admin ID who approved the user
  approvalRequestId?: string; // ID of the approval request that was approved
  kasaBalanceUSD?: number; // Kasa (Cash Register) balance in USD
  kasaBalanceTRY?: number; // Kasa (Cash Register) balance in TRY
}

export interface Office {
  id: string;
  name: string;
  email: string;
  username?: string; // Username for login
  password?: string; // Password for login (should be hashed in production)
  createdAt: string;
  createdBy: string; // SuperAdmin who created it
  archived?: boolean; // Whether the office is archived (soft delete)
  archivedAt?: string; // Timestamp when the office was archived
}

export enum PostStatus {
    Active = 'Active',
    Paused = 'Paused',
    Completed = 'Completed'
}

export interface ShipmentPost {
  id:string;
  totalKg: number;
  pricePerKg: number;
  officePaymentPerQuantity?: number; // How much the office pays customers per quantity
  officePaymentPerQuantityCurrency?: string; // Currency for office payment (USD or TRY)
  maliyet?: number; // Cost per kg (from product)
  transportationFee?: number; // Taşıma Ücreti - Transportation fee per kg/piece
  transportationFeeCurrency?: string; // Currency for transportation fee (USD or TRY)
  unit?: 'kg' | 'piece'; // Unit: kg or piece
  pieceWeight?: number; // Weight per piece (optional, only when unit is 'piece')
  fromCountry: string;
  fromCity: string;
  toCountry: string;
  toCity: string;
  containerNumber?: string; // Container number
  notes?: string;
  status: PostStatus;
  createdAt: string;
  adminId: string; // The admin who created it
  officeId: string; // The office that owns this post
  archived?: boolean; // Whether the post is archived (soft delete)
  archivedAt?: string; // Timestamp when the post was archived
}

export enum RequestStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Delivered = 'Delivered',
    InTransit = 'InTransit',
    IssueFlagged = 'IssueFlagged',
    Canceled = 'Canceled',
    Transferred = 'Transferred'
}

export interface StatusHistoryEntry {
  status: RequestStatus;
  timestamp: string;
}

export interface ReservationRequest {
  id: string;
  userId: string;
  postId: string;
  requestedKg: number;
  status: RequestStatus;
  createdAt: string;
  qrCodeData?: string;
  packageId?: string;
  statusHistory?: StatusHistoryEntry[];
  // Worker tracking for actions
  approvedBy?: string; // User/Worker ID who approved the request
  qrGeneratedBy?: string; // Worker ID who generated QR code
  receivedBy?: string; // Worker ID who received the package
  deliveredBy?: string; // Worker ID who marked as delivered
  scannedBy?: string; // Worker ID who scanned the QR code
  // Transfer tracking
  transferredFromUserId?: string; // User ID who transferred the package
  transferredAt?: string; // Timestamp when package was transferred
  transferredBy?: string; // User ID who performed the transfer (giver)
  archived?: boolean; // Whether the request is archived (soft delete)
  archivedAt?: string; // Timestamp when the request was archived
  // Delivery tracking
  receivedKg?: number; // Actual weight received when package was delivered (may differ from requestedKg)
  // Bag number for manual packages
  bagNumber?: string; // Bag number (Çuval Numarası) for manual packages
}

export interface Package {
  id: string;
  requestId: string;
  qrCodeData: string;
  createdAt: string;
}

export enum TransactionType {
    Credit = 'Credit',
    Withdrawal = 'Withdrawal'
}

export interface BalanceTransaction {
    id: string;
    userId: string;
    amount: number;
    type: TransactionType;
    date: string;
    description: string;
    archived?: boolean; // Whether the transaction is archived (soft delete)
    archivedAt?: string; // Timestamp when the transaction was archived
}

export enum LostPackageReportStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected'
}

export interface LostPackageReport {
    id: string;
    requestId: string;
    userId: string;
    description: string;
    status: LostPackageReportStatus;
    createdAt: string;
    reviewedAt?: string;
    reviewedBy?: string;
    adminNotes?: string;
    fileUrl?: string;
    fileName?: string;
    archived?: boolean; // Whether the report is archived (soft delete)
    archivedAt?: string; // Timestamp when the report was archived
}

export enum WithdrawalRequestStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected'
}

export interface WithdrawalRequest {
    id: string;
    userId: string;
    officeId: string;
    amount: number;
  // Optional currency for the withdrawal (USD or TRY). Older records may not have this.
  currency?: string;
    status: WithdrawalRequestStatus;
    createdAt: string;
    reviewedAt?: string;
    reviewedBy?: string; // Office admin ID who reviewed it
    adminNotes?: string;
    archived?: boolean; // Whether the withdrawal request is archived (soft delete)
    archivedAt?: string; // Timestamp when the withdrawal request was archived
}

export interface PendingTransfer {
    id: string;
    requestId: string;
    giverId: string;
    receiverId: string;
    giverConfirmed: boolean;
    receiverConfirmed: boolean;
    createdAt: string;
    completedAt?: string;
    status: 'pending' | 'completed' | 'cancelled';
    archived?: boolean; // Whether the transfer is archived (soft delete)
    archivedAt?: string; // Timestamp when the transfer was archived
}

export interface Seller {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    notes?: string;
    createdAt: string;
    adminId: string; // The admin who created it
    officeId?: string; // The office that created it (for office accounts)
    archived?: boolean; // Whether the seller is archived (soft delete)
    archivedAt?: string; // Timestamp when the seller was archived
}

export interface SellerPayment {
    id: string;
    sellerId: string;
    amount: number;
    date: string;
    description?: string;
    createdBy: string; // Admin ID who recorded the payment
    createdAt: string;
    archived?: boolean; // Whether the payment is archived (soft delete)
    archivedAt?: string; // Timestamp when the payment was archived
}

export interface Product {
    id: string;
    sellerId: string;
    name: string;
    description?: string;
    totalKg: number;
    totalUnit?: 'kg' | 'piece'; // Total unit: kg or piece
    pieceWeight?: number; // Weight per piece (optional, only when totalUnit is 'piece')
    pricePerKg: number;
    priceUnit?: 'kg' | 'piece'; // Pricing unit: per kg or per piece
    pricePerKgCurrency?: string; // Currency for price (USD or TRY)
    maliyet?: number; // Cost per kg
    maliyetCurrency?: string; // Currency for cost (USD or TRY)
    transportationFee?: number; // Taşıma Ücreti - Transportation fee per kg/piece
    transportationFeeCurrency?: string; // Currency for transportation fee (USD or TRY)
    paymentType?: 'before' | 'after'; // Payment type: before or after delivery
    fromCountry: string;
    fromCity: string;
    toCountry: string;
    toCity: string;
    containerNumber?: string; // Container number
    notes?: string;
    createdAt: string;
    archived?: boolean; // Whether the product is archived (soft delete)
    archivedAt?: string; // Timestamp when the product was archived
}

export enum ActivityType {
    RequestApproved = 'RequestApproved',
    RequestRejected = 'RequestRejected',
    PackageDelivered = 'PackageDelivered',
    QRGenerated = 'QRGenerated',
    StatusChanged = 'StatusChanged',
    LostReportApproved = 'LostReportApproved',
    LostReportRejected = 'LostReportRejected',
    PackageTransferred = 'PackageTransferred',
    PostCreated = 'PostCreated',
    PostUpdated = 'PostUpdated',
    UserCreated = 'UserCreated',
    BalanceAdjusted = 'BalanceAdjusted'
}

export interface ActivityLog {
    id: string;
    type: ActivityType;
    performedBy: string; // User ID who performed the action
    workerId?: string; // Worker ID who performed the action (if done by a worker)
    targetUserId?: string; // User ID affected by the action
    targetRequestId?: string; // Request ID if related to a package
    targetPostId?: string; // Post ID if related to a shipment
    description: string;
    details?: any; // Additional details as JSON
    createdAt: string;
}

export enum UserApprovalRequestStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected'
}

export interface UserApprovalRequest {
    id: string;
    userId: string;
    officeId: string;
    status: UserApprovalRequestStatus;
    message?: string; // Optional message from user
    fileUrl?: string; // URL of attached file
    fileName?: string; // Name of attached file
    createdAt: string;
    reviewedAt?: string; // Timestamp when reviewed
    reviewedBy?: string; // Office ID or admin ID who reviewed it
    adminNotes?: string; // Notes from the reviewer
    archived?: boolean; // Whether the request is archived (soft delete)
    archivedAt?: string; // Timestamp when the request was archived
}

export enum ShortageDeliveryStatus {
    Pending = 'Pending',           // Eksik teslimat bildirimi beklemede
    OnHold = 'OnHold',             // Beklemeye alındı - eksik miktar getirilecek
    BalanceDeducted = 'BalanceDeducted', // Bakiyeden düşüldü
    PaidUpfront = 'PaidUpfront',   // Peşin ödendi
    Completed = 'Completed',       // Eksik miktar getirildi ve teslim edildi
    Rejected = 'Rejected'          // Reddedildi
}

export interface ShortageDelivery {
    id: string;
    requestId: string;
    userId: string;                // Paketi veren kullanıcı
    postId: string;
    sendingOfficeId: string;        // Paketi veren ofis (post.officeId)
    receivingOfficeId: string;      // Paketi teslim alan ofis
    requestedKg: number;
    receivedKg: number;
    missingKg: number;              // requestedKg - receivedKg
    status: ShortageDeliveryStatus;
    createdAt: string;
    reviewedAt?: string;
    reviewedBy?: string;            // Ofis kullanıcı ID
    action?: 'deduct' | 'hold';     // Seçilen aksiyon
    missingKgDelivered?: number;    // Getirilen eksik miktar
    missingKgDeliveredAt?: string;
    finalAction?: 'deduct' | 'pay'; // Beklemeye alındıktan sonra seçilen aksiyon
    finalActionApprovedBy?: string; // Karşılıklı onay veren kullanıcı
    finalActionApprovedAt?: string;
    notes?: string;
    archived?: boolean;
    archivedAt?: string;
}

export enum NotificationType {
    PackageApproval = 'PackageApproval',           // Customer: Package approved
    PackageReceiving = 'PackageReceiving',        // Customer: Package received/delivered
    BalanceNotification = 'BalanceNotification',  // Customer: Balance changed
    AdminReminder = 'AdminReminder',               // Customer: Reminder from admin
    PackageRequest = 'PackageRequest',            // Office: New package request
    AccountApprovalRequest = 'AccountApprovalRequest' // Office: New account approval request
}

export interface Notification {
    id: string;
    userId: string;                // Target user ID
    type: NotificationType;
    title: string;
    message: string;
    read: boolean;
    createdAt: string;
    readAt?: string;
    relatedId?: string;            // Related request/package/transaction ID
    data?: any;                    // Additional data
    archived?: boolean;
    archivedAt?: string;
}